import type { BreadcrumbItem } from '../location/types';

/** 几何单位为整数毫米（架构书 §10.1）——画布只在渲染时乘以缩放比例 */
export interface Geometry {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly rotation: number;
  readonly z: number;
  readonly color: string | null;
  readonly locked: boolean;
  readonly version: number;
}

export interface LayoutObject {
  readonly locationId: string;
  readonly name: string;
  readonly typeId: string | null;
  readonly canStoreInventory: boolean;
  readonly hasChildren: boolean;
  readonly locationVersion: number;
  readonly geometry: Geometry;
}

export interface LayoutLeaseInfo {
  readonly userId: string;
  readonly userName: string;
  readonly expiresAt: string;
  readonly isMine: boolean;
}

export interface LayoutScene {
  readonly parentId: string | null;
  readonly breadcrumb: readonly BreadcrumbItem[];
  readonly objects: readonly LayoutObject[];
  readonly lease: LayoutLeaseInfo | null;
}

export interface GeometryOp {
  readonly locationId: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly locked?: boolean;
  readonly version: number;
}

export type GeometryResult =
  | { readonly locationId: string; readonly ok: true; readonly version: number }
  | {
      readonly locationId: string;
      readonly ok: false;
      readonly code: string;
      readonly current?: Geometry;
    };

/** 8 向缩放手柄（Spec §7：横向 / 纵向 / 角） */
export const RESIZE_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
export type ResizeHandle = (typeof RESIZE_HANDLES)[number];

/** 最小对象尺寸，与数据库 CHECK (w > 0 AND h > 0) 一致但更保守（1 mm 的矩形没法点中） */
export const MIN_SIZE_MM = 100;

/**
 * 【U-04】"点一下就建一个"的默认尺寸（毫米）。
 *
 * 此前点击（拖拽位移≈0）会落到 `clampSize` 的下限 100mm——在默认 5% 缩放下
 * 只有 5px，用户几乎看不见新建的位置，于是以为"必须精确拖拽才能创建"。
 * 1m×1m 是仓库里一个货位的合理起点，建完还能在属性面板精确改尺寸。
 */
export const DEFAULT_CREATE_SIZE_MM = 1000;

/** 拖拽位移小于这个毫米数就当成"点击"，走默认尺寸路径 */
export const CLICK_CREATE_THRESHOLD_MM = 50;

/**
 * 【U-04】把"抬手那一刻"的创建交互解析成最终矩形。
 *
 * 两条路径：
 *  - 拖拽（任一边 ≥ CLICK_CREATE_THRESHOLD_MM）：用户拉出的矩形，各边再过一次
 *    clampSize 保证不低于 MIN_SIZE_MM；
 *  - 点击（几乎没有位移）：以按下点为中心落一个 DEFAULT_CREATE_SIZE_MM 方块，
 *    这样"不想精确拖拽"的用户点一下就能建出一个看得见、可再调的位置。
 *
 * 抽成纯函数是为了能脱离画布组件直接单测（同 computeCommitChanges 的做法）。
 */
export function resolveCreatedRect(
  origin: { readonly x: number; readonly y: number },
  rect: { readonly x: number; readonly y: number; readonly w: number; readonly h: number },
): { x: number; y: number; w: number; h: number } {
  if (rect.w < CLICK_CREATE_THRESHOLD_MM && rect.h < CLICK_CREATE_THRESHOLD_MM) {
    const size = DEFAULT_CREATE_SIZE_MM;
    return {
      x: Math.round(origin.x - size / 2),
      y: Math.round(origin.y - size / 2),
      w: size,
      h: size,
    };
  }
  return { x: rect.x, y: rect.y, w: clampSize(rect.w), h: clampSize(rect.h) };
}

export function clampSize(value: number): number {
  return Math.max(MIN_SIZE_MM, Math.round(value));
}

/** 矩形中心是否落在另一个矩形内——"拖入即提示改父"的判定（§7 红线的触发条件） */
export function centerInside(
  inner: Pick<Geometry, 'x' | 'y' | 'w' | 'h'>,
  outer: Pick<Geometry, 'x' | 'y' | 'w' | 'h'>,
): boolean {
  const cx = inner.x + inner.w / 2;
  const cy = inner.y + inner.h / 2;
  return cx >= outer.x && cx <= outer.x + outer.w && cy >= outer.y && cy <= outer.y + outer.h;
}

export function applyResize(
  start: Pick<Geometry, 'x' | 'y' | 'w' | 'h'>,
  handle: ResizeHandle,
  dx: number,
  dy: number,
): { x: number; y: number; w: number; h: number } {
  let { x, y, w, h } = start;
  if (handle.includes('e')) w = clampSize(start.w + dx);
  if (handle.includes('s')) h = clampSize(start.h + dy);
  if (handle.includes('w')) {
    w = clampSize(start.w - dx);
    x = start.x + start.w - w;
  }
  if (handle.includes('n')) {
    h = clampSize(start.h - dy);
    y = start.y + start.h - h;
  }
  return { x: Math.round(x), y: Math.round(y), w, h };
}

/** 两个矩形是否有重叠（用于框选命中测试） */
export function rectsIntersect(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** 一组几何的包围盒——多选整体移动/缩放、等距分布的参照矩形 */
export function boundingBoxOf(geoms: readonly Pick<Geometry, 'x' | 'y' | 'w' | 'h'>[]): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const minX = Math.min(...geoms.map((g) => g.x));
  const minY = Math.min(...geoms.map((g) => g.y));
  const maxX = Math.max(...geoms.map((g) => g.x + g.w));
  const maxY = Math.max(...geoms.map((g) => g.y + g.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * 多选整体缩放（roadmap M3b）：先把包围盒本身按常规 8 向缩放算出新尺寸，
 * 再按同一个缩放比例把每个成员相对包围盒左上角的位置和尺寸等比例变换。
 */
export function applyGroupResize(
  box: { x: number; y: number; w: number; h: number },
  handle: ResizeHandle,
  dx: number,
  dy: number,
  members: ReadonlyMap<string, Geometry>,
): Map<string, { x: number; y: number; w: number; h: number }> {
  const newBox = applyResize(box, handle, dx, dy);
  const scaleX = box.w === 0 ? 1 : newBox.w / box.w;
  const scaleY = box.h === 0 ? 1 : newBox.h / box.h;
  const result = new Map<string, { x: number; y: number; w: number; h: number }>();
  for (const [id, g] of members) {
    const w = clampSize(Math.round(g.w * scaleX));
    const h = clampSize(Math.round(g.h * scaleY));
    const x = Math.round(newBox.x + (g.x - box.x) * scaleX);
    const y = Math.round(newBox.y + (g.y - box.y) * scaleY);
    result.set(id, { x, y, w, h });
  }
  return result;
}

/** 复制/粘贴的剪贴板负载（roadmap M3b："复制/粘贴（剪贴板 JSON）"） */
export interface ClipboardPayload {
  readonly kind: 'wms-layout-objects';
  readonly items: ReadonlyArray<{
    readonly name: string;
    readonly typeId: string | null;
    readonly canStoreInventory: boolean;
    readonly color: string | null;
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  }>;
}

/**
 * M3B 独立复审第二轮 NEW-4（LOW）：只校验 `kind`/`items` 是数组是不够的——
 * 服务端当然会校验每一项（不安全边界靠这个），但明显畸形的条目（比如
 * `{}`）会让粘贴发出一批注定全部失败的请求，体验上毫无必要。这里补上
 * 每一项的最基本形状校验：字段存在且类型对，不校验取值范围/业务规则
 * （那是服务端的事）。
 */
function isClipboardItem(value: unknown): value is ClipboardPayload['items'][number] {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.name === 'string' &&
    v.name.length > 0 &&
    (v.typeId === null || typeof v.typeId === 'string') &&
    typeof v.canStoreInventory === 'boolean' &&
    (v.color === null || typeof v.color === 'string') &&
    // M3B 独立复审第三轮（LOW，NEW-7）：`typeof === 'number'` 会放过
    // Infinity/-Infinity——`JSON.parse('{"x":1e999}')` 的结果就是 Infinity，
    // 这条路径是真实可达的（NaN 反而不可达：JSON.parse 直接抛错）。
    // Number.isFinite 一并排除三者。
    Number.isFinite(v.x) &&
    Number.isFinite(v.y) &&
    Number.isFinite(v.w) &&
    Number.isFinite(v.h)
  );
}

export function isClipboardPayload(value: unknown): value is ClipboardPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { kind?: unknown }).kind === 'wms-layout-objects' &&
    Array.isArray((value as { items?: unknown }).items) &&
    (value as { items: unknown[] }).items.every(isClipboardItem)
  );
}
